/**
 * /sales-toolkit V2 — Field Sales Command Center
 *
 * Hidden, mobile-first ops tool. NOT linked in Nav or Footer.
 * Dark mode only. Designed for use in industrial-park parking lots.
 *
 * V2 upgrades:
 *  - Maps button → 3-park popup sheet
 *  - Notes button → slide-up notes panel with localStorage
 *  - One-tap "+ Create Profile" modal per plant → posts to backend
 *  - "Share Profile" button per plant
 *  - 4th stat card "RFQs This Week" (live from /api/sales-toolkit/stats)
 *  - Competitor alert strip
 *  - Dynamic "Day's Mission" header
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Copy, Check, MapPin, ExternalLink, MessageCircle, FileText,
  ChevronDown, ChevronUp, Plus, Share2, X,
} from "lucide-react";
import api from "../api";

const STORAGE_KEY = "cd_sales_toolkit_v1";
const NOTES_KEY = "cd_field_notes";
const REF_KEY = "cd_sales_ref";  // founder's ref handle, attached to share links

const INTEL_LINES = [
  "57.6% — average US tariff on Chinese manufactured goods (2026)",
  "330+ IMMEX plants operate in the Chihuahua–El Paso corridor (Juárez, Parral, Chihuahua City)",
  "US nearshoring RFQs up 340% since 2023",
  "USMCA utilization nearly doubled in 2025",
];

const PARKS = [
  { name: "Parque Industrial Bermúdez", url: "https://maps.google.com/?q=Parque+Industrial+Bermudez+Juarez+Mexico" },
  { name: "Parque Industrial Salvarcar", url: "https://maps.google.com/?q=Parque+Industrial+Salvarcar+Juarez+Mexico" },
  { name: "Parque Industrial Aeropuerto", url: "https://maps.google.com/?q=Parque+Industrial+Aeropuerto+Juarez+Mexico" },
];

const PLANTS = [
  { id: 1, name: "Marelli Automotive", park: "Bermúdez", capability: "Precision CNC Machining", score: 88 },
  { id: 2, name: "APTIV Río Bravo", park: "Bermúdez", capability: "Electrical / Harness", score: 85 },
  { id: 3, name: "MAHLE Behr Ric.", park: "Bermúdez", capability: "Automotive Thermal", score: 85 },
  { id: 4, name: "Moldes de la Frontera", park: "Bermúdez", capability: "Injection Molding", score: 80 },
  { id: 5, name: "Tecma Planta 28", park: "Bermúdez", capability: "Mixed Manufacturing", score: 80 },
  { id: 6, name: "BMP Technologies", park: "Salvarcar", capability: "Injection Molding", score: 80 },
  { id: 7, name: "First Brands Group", park: "Salvarcar", capability: "Flexible / Rigid Mfg", score: 75 },
  { id: 8, name: "Prince Manufacturing", park: "Aeropuerto", capability: "Metal Stamping", score: 75 },
  { id: 9, name: "Hopkins Manufacturing", park: "Aeropuerto", capability: "Metal Fabrication", score: 55 },
  { id: 10, name: "COFICAB Juárez", park: "Bermúdez", capability: "Automotive Cabling", score: 85 },
  { id: 11, name: "AKWEL Juárez", park: "Bermúdez", capability: "Automotive Parts", score: 80 },
  { id: 12, name: "Lear San Lorenzo", park: "Salvarcar", capability: "Automotive Seating", score: 85 },
  { id: 13, name: "Hubbell Planta", park: "Aeropuerto", capability: "Power Distribution", score: 65 },
  { id: 14, name: "Strattec Security", park: "Bermúdez", capability: "Precision Stamping", score: 80 },
  { id: 15, name: "GCC Planta Juárez", park: "Bermúdez", capability: "Cement / Concrete", score: 30 },
];

const PITCHES = [
  { id: "vault", label: "Pitch 1 · The Vault Pitch (Cold Open)",
    body: "¿Todavía envías tus certificados ISO por correo como adjunto? Con Corridor Digital, mandas un link seguro con fecha de vencimiento. Tu cliente en Detroit lo abre en segundos. Y tú sabes exactamente cuándo lo vio. Gratis para empezar — sin tarjeta." },
  { id: "tariff", label: "Pitch 2 · The Tariff Pitch (If They Ask Why Now)",
    body: "Los aranceles de EE.UU. sobre China están al 57%. Los compradores americanos tienen órdenes de sus CFOs de encontrar proveedores mexicanos este año. El problema es que no te pueden encontrar. Corridor Digital te hace visible en 10 minutos." },
  { id: "social", label: "Pitch 3 · The Social Proof Pitch (After First Signed Plant)",
    body: "[Plant Name] en Bermúdez se registró la semana pasada. Su Director Comercial dijo que fue la decisión más fácil del año — ahora comparte su perfil con clientes en EE.UU. en lugar de adjuntar PDFs. ¿Quieres ver cómo se ve tu perfil?" },
];

const OBJECTIONS = [
  { q: '"No tenemos tiempo"', a: '"Son 10 minutos. Yo lo configuro aquí mismo."' },
  { q: '"Ya tenemos clientes"', a: '"Perfecto — esto es para los clientes que todavía no te conocen."' },
  { q: '"¿Cuánto cuesta?"', a: '"Gratis para empezar. Pro es $199/mes — menos que un anuncio en directorio industrial."' },
];

const FOLLOWUPS = [
  { id: "m1", label: "M1 · Same Day (within 2 hours of visit)",
    body: "Hola [Nombre] 👋 Fue un placer conocerte hoy en [Parque]. Aquí está tu perfil en Corridor Digital: [link]. Compártelo con tus contactos en EE.UU. en lugar de adjuntar tus certificados — se ve mucho más profesional." },
  { id: "m2", label: "M2 · Day 3 (3 days after visit)",
    body: "Hola [Nombre], ¿pudiste compartir tu perfil con tus clientes en EE.UU.? Esta semana tuvimos compradores de Texas y Michigan buscando capacidad CNC en Juárez. Tu perfil está listo para recibirlos." },
  { id: "m3", label: "M3 · Day 7 (Upgrade trigger)",
    body: "Hola [Nombre], tu perfil tuvo visitas esta semana. Hay compradores buscando exactamente lo que produces. Actualiza a Pro para ver quién te está buscando y recibir cotizaciones directamente: corridordigital.net/pricing" },
];

function loadState() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); } catch { return {}; }
}
function saveState(s) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* noop */ }
}
function slugify(s) {
  return (s || "")
    .normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function SalesToolkit() {
  const [state, setState] = useState(loadState);
  const [intelIdx, setIntelIdx] = useState(0);
  const [mapsOpen, setMapsOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notes, setNotes] = useState(() => {
    try { return localStorage.getItem(NOTES_KEY) || ""; } catch { return ""; }
  });
  const [signupPlant, setSignupPlant] = useState(null);
  const [refHandle, setRefHandleState] = useState(() => {
    try { return localStorage.getItem(REF_KEY) || "derick"; } catch { return "derick"; }
  });
  function setRefHandle(v) {
    const clean = (v || "").trim().toLowerCase().replace(/[^a-z0-9_-]/g, "").slice(0, 30) || "derick";
    setRefHandleState(clean);
    try { localStorage.setItem(REF_KEY, clean); } catch { /* noop */ }
  }
  const [stats, setStats] = useState({
    rfqs_last_7d: 4,  // hardcoded fallback per brief
    plants_in_directory: 8,
    field_signups_last_7d: 0,
  });

  useEffect(() => {
    document.title = "Field Toolkit — Corridor Digital Internal";
    const prev = document.body.style.background;
    document.body.style.background = "#0a0f1e";
    return () => { document.body.style.background = prev; };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setIntelIdx((i) => (i + 1) % INTEL_LINES.length), 5000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => { saveState(state); }, [state]);
  useEffect(() => {
    try { localStorage.setItem(NOTES_KEY, notes); } catch { /* noop */ }
  }, [notes]);

  // Live stats — falls back to hardcoded values if backend is unreachable
  useEffect(() => {
    api.get("/sales-toolkit/stats")
      .then(({ data }) => setStats(data))
      .catch(() => { /* keep hardcoded */ });
  }, []);

  function setStatus(plantId, status) {
    setState((s) => ({ ...s, plants: { ...(s.plants || {}), [plantId]: { ...(s.plants?.[plantId] || {}), status } } }));
  }
  function getStatus(plantId) { return state.plants?.[plantId]?.status || "not_visited"; }
  function toggleFollowup(plantId, msgId) {
    setState((s) => ({
      ...s,
      plants: {
        ...(s.plants || {}),
        [plantId]: {
          ...(s.plants?.[plantId] || {}),
          followups: { ...((s.plants?.[plantId]?.followups) || {}), [msgId]: !(s.plants?.[plantId]?.followups?.[msgId]) },
        },
      },
    }));
  }
  function getFollowup(plantId, msgId) { return !!state.plants?.[plantId]?.followups?.[msgId]; }

  const visitedCount = useMemo(
    () => PLANTS.reduce((acc, p) => acc + (["visited", "signed"].includes(state.plants?.[p.id]?.status) ? 1 : 0), 0),
    [state]
  );
  const signedCount = useMemo(
    () => PLANTS.reduce((acc, p) => acc + (state.plants?.[p.id]?.status === "signed" ? 1 : 0), 0),
    [state]
  );

  const mission = useMemo(() => {
    if (signedCount >= 2) return "🚀 You're building something real. Keep going.";
    if (signedCount >= 1) return "🏆 Mission complete. Every plant after this is a bonus.";
    if (visitedCount >= 1) return "💪 Keep going. You need 1 signed plant today.";
    return "🎯 Mission: Get 3 conversations. Come back with 1 signed.";
  }, [visitedCount, signedCount]);

  function STATS_CARDS() {
    return [
      { label: "Active US Buyers (7d)", value: stats.rfqs_last_7d, testid: "stat-buyers", green: stats.rfqs_last_7d > 0 },
      { label: "Plants in Directory", value: stats.plants_in_directory, testid: "stat-plants" },
      { label: "Top Searched Capability", value: "CNC", testid: "stat-cap" },
      { label: "RFQs This Week", value: stats.rfqs_last_7d, testid: "stat-rfqs", green: stats.rfqs_last_7d > 0 },
    ];
  }

  return (
    <div className="st-root" data-testid="sales-toolkit-page">
      <Style />

      {/* TOP BADGE */}
      <div className="st-badge" data-testid="st-badge">
        ◆ CORRIDOR DIGITAL INTERNAL · FIELD TOOLKIT
      </div>

      {/* REF HANDLE — appended to share URLs as ?ref=<handle> for FOMO tracking */}
      <div className="st-ref-handle" data-testid="st-ref-handle-wrap">
        <label htmlFor="st-ref-input">SHARE AS:</label>
        <span className="st-ref-prefix">?ref=</span>
        <input
          id="st-ref-input"
          value={refHandle}
          onChange={(e) => setRefHandle(e.target.value)}
          spellCheck={false}
          autoCapitalize="none"
          className="st-ref-input"
          data-testid="st-ref-handle"
        />
      </div>

      {/* COMPETITOR ALERT STRIP */}
      <div className="st-alert" data-testid="st-competitor-alert">
        ⚡ First-mover window open · no verified competitor directory exists for the Chihuahua–El Paso corridor (Juárez, Parral, Chihuahua City).
      </div>

      {/* DAY'S MISSION */}
      <div className="st-mission" data-testid="st-mission">
        {mission}
      </div>

      {/* SECTION 1 — Live Intelligence */}
      <section className="st-section" data-testid="st-intelligence">
        <h2 className="st-h2">Live intelligence</h2>
        <div className="st-stats st-stats-4">
          {STATS_CARDS().map((s) => (
            <div key={s.testid} className={`st-stat ${s.green ? "st-stat-green" : ""}`} data-testid={s.testid}>
              <div className="st-stat-value">{s.value}</div>
              <div className="st-stat-label">{s.label}</div>
            </div>
          ))}
        </div>
        <div className="st-intel" data-testid="st-intel-line">
          <span className="st-intel-dot" />
          <span>{INTEL_LINES[intelIdx]}</span>
        </div>
      </section>

      {/* SECTION 2 — Plant Hit List */}
      <section className="st-section" data-testid="st-hitlist">
        <h2 className="st-h2">Target plants today</h2>
        <div className="st-progress" data-testid="st-progress">
          <div className="st-progress-bar" style={{ width: `${(visitedCount / PLANTS.length) * 100}%` }} />
          <div className="st-progress-label">
            {visitedCount} / {PLANTS.length} visited · {signedCount} signed
          </div>
        </div>
        <div className="st-plants">
          {PLANTS.map((p) => (
            <PlantCard
              key={p.id}
              plant={p}
              status={getStatus(p.id)}
              onStatusChange={(s) => setStatus(p.id, s)}
              onOpenSignup={() => setSignupPlant(p)}
              followups={{ m1: getFollowup(p.id, "m1"), m2: getFollowup(p.id, "m2"), m3: getFollowup(p.id, "m3") }}
              onFollowupToggle={(m) => toggleFollowup(p.id, m)}
            />
          ))}
        </div>
      </section>

      {/* SECTION 3 — Pitch Arsenal */}
      <section className="st-section" data-testid="st-pitches">
        <h2 className="st-h2">Pitch arsenal</h2>
        {PITCHES.map((p) => (
          <CopyCard key={p.id} label={p.label} body={p.body} testid={`pitch-${p.id}`} />
        ))}
        <details className="st-objections" data-testid="st-objections">
          <summary>Objection handler · tap to expand</summary>
          {OBJECTIONS.map((o, i) => (
            <div key={i} className="st-objection">
              <div className="st-obj-q">{o.q}</div>
              <div className="st-obj-a">{o.a}</div>
            </div>
          ))}
        </details>
      </section>

      {/* SECTION 4 — Follow-up Sequence */}
      <section className="st-section" data-testid="st-followups">
        <h2 className="st-h2">WhatsApp follow-up sequence</h2>
        <div className="st-followup-hint">
          Use the checkboxes inside each plant card above to track which messages you've sent per contact.
        </div>
        {FOLLOWUPS.map((f) => (
          <CopyCard key={f.id} label={f.label} body={f.body} testid={`followup-${f.id}`} />
        ))}
      </section>

      {/* SECTION 5 — ROI Snapshot */}
      <section className="st-section" data-testid="st-roi">
        <h2 className="st-h2">ROI snapshot</h2>
        <div className="st-roi-grid">
          <div className="st-roi-card st-roi-bad">
            <div className="st-roi-title">🇨🇳 China supplier</div>
            <ul>
              <li><span>Tariff</span><strong>57.6%</strong></li>
              <li><span>Lead time</span><strong>45–90 days</strong></li>
              <li><span>Shipping</span><strong>$8,000+/container</strong></li>
              <li><span>Risk</span><strong>High (geopolitical)</strong></li>
            </ul>
          </div>
          <div className="st-roi-card st-roi-good">
            <div className="st-roi-title">🇲🇽 Juárez supplier</div>
            <ul>
              <li><span>Tariff</span><strong>0–5% (USMCA)</strong></li>
              <li><span>Lead time</span><strong>3–10 days</strong></li>
              <li><span>Shipping</span><strong>$800 truck</strong></li>
              <li><span>Risk</span><strong>Low (USMCA stable)</strong></li>
            </ul>
          </div>
        </div>
        <div className="st-roi-line">
          Your buyer's CFO already made this decision. They just need to find you.
        </div>
      </section>

      <div className="st-spacer" />

      {/* SECTION 6 — Sticky Footer */}
      <nav className="st-footer" data-testid="st-footer">
        <FooterBtn
          icon={<Copy size={20} />}
          label="Pitch"
          onClick={() => copyAndFlash(PITCHES[0].body, "footer-copy-pitch")}
          testid="footer-copy-pitch"
        />
        <FooterBtn
          icon={<MapPin size={20} />}
          label="Maps"
          onClick={() => setMapsOpen(true)}
          testid="footer-maps"
        />
        <FooterBtn
          icon={<ExternalLink size={20} />}
          label="Directory"
          onClick={() => window.open("/directory", "_blank")}
          testid="footer-directory"
        />
        <FooterBtn
          icon={<FileText size={20} />}
          label="Notes"
          onClick={() => setNotesOpen(true)}
          testid="footer-notes"
        />
      </nav>

      {/* SHEETS / MODALS */}
      {mapsOpen && (
        <BottomSheet onClose={() => setMapsOpen(false)} testid="maps-sheet">
          <div className="st-sheet-title">Open in Google Maps</div>
          <div className="st-sheet-options">
            {PARKS.map((p) => (
              <button
                key={p.name}
                onClick={() => { window.open(p.url, "_blank"); setMapsOpen(false); }}
                className="st-sheet-option"
                data-testid={`maps-${slugify(p.name)}`}
              >
                <MapPin size={18} />
                <span>{p.name}</span>
              </button>
            ))}
          </div>
        </BottomSheet>
      )}

      {notesOpen && (
        <BottomSheet onClose={() => setNotesOpen(false)} testid="notes-sheet">
          <div className="st-sheet-title">Field notes</div>
          <textarea
            className="st-notes-input"
            placeholder="Quick notes — plant name, contact, what they said..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="notes-textarea"
          />
          <div className="st-notes-actions">
            <button
              className="st-notes-copy"
              onClick={async () => {
                try { await navigator.clipboard.writeText(notes); } catch { /* noop */ }
              }}
              data-testid="notes-copy"
            >
              <Copy size={14} /> Copy notes
            </button>
            <button
              className="st-notes-clear"
              onClick={() => {
                if (window.confirm("Clear all notes?")) setNotes("");
              }}
              data-testid="notes-clear"
            >
              Clear
            </button>
          </div>
        </BottomSheet>
      )}

      {signupPlant && (
        <SignupModal
          plant={signupPlant}
          onClose={() => setSignupPlant(null)}
          onSuccess={() => {
            setStatus(signupPlant.id, "signed");
            setSignupPlant(null);
          }}
        />
      )}
    </div>
  );
}

function PlantCard({ plant, status, onStatusChange, onOpenSignup, followups, onFollowupToggle }) {
  const [expanded, setExpanded] = useState(false);
  const scoreColor =
    plant.score >= 85 ? "st-score-priority"
    : plant.score >= 60 ? "st-score-secondary"
    : "st-score-skip";
  const scoreLabel = plant.score >= 85 ? "PRIORITY" : plant.score >= 60 ? "SECONDARY" : "SKIP";

  function whatsappOutreach() {
    const msg = encodeURIComponent(
      `Hola, soy Derick de Corridor Digital. Estoy visitando plantas en ${plant.park} esta semana — ${plant.name} salió en mi lista. ¿Tienes 10 minutos esta semana para enseñarte cómo se vería su perfil bilingüe en corridordigital.net?`
    );
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  }
  function shareProfile() {
    const slug = slugify(plant.name);
    const ref = (typeof localStorage !== "undefined" && localStorage.getItem(REF_KEY)) || "derick";
    const link = `https://corridordigital.net/plants/${slug}?ref=${encodeURIComponent(ref)}`;
    const msg = encodeURIComponent(
      `Hola ${plant.name} — Aquí está tu perfil en Corridor Digital: ${link} — compártelo con tus clientes en EE.UU. en lugar de adjuntar tus certificados. Se ve mucho más profesional.`
    );
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  }

  return (
    <div
      className={`st-plant ${status === "signed" ? "st-plant-signed" : status === "visited" ? "st-plant-visited" : ""}`}
      data-testid={`plant-${plant.id}`}
    >
      <div className="st-plant-top">
        <div className="st-plant-main">
          <div className="st-plant-name">{plant.name}</div>
          <div className="st-plant-meta">
            <span>{plant.park}</span>
            <span className="st-plant-dot">·</span>
            <span>{plant.capability}</span>
          </div>
          <button
            onClick={onOpenSignup}
            className="st-create-profile"
            data-testid={`plant-${plant.id}-create`}
          >
            <Plus size={14} /> Create Profile
          </button>
        </div>
        <div className={`st-score ${scoreColor}`} data-testid={`plant-score-${plant.id}`}>
          <div className="st-score-num">{plant.score}</div>
          <div className="st-score-label">{scoreLabel}</div>
        </div>
      </div>

      <div className="st-plant-actions">
        <div className="st-status-group" role="group">
          <StatusButton active={status === "not_visited"} onClick={() => onStatusChange("not_visited")} testid={`plant-${plant.id}-notvisited`}>Not visited</StatusButton>
          <StatusButton active={status === "visited"} onClick={() => onStatusChange("visited")} testid={`plant-${plant.id}-visited`}>Visited</StatusButton>
          <StatusButton active={status === "signed"} onClick={() => onStatusChange("signed")} testid={`plant-${plant.id}-signed`}>Signed</StatusButton>
        </div>
      </div>

      <div className="st-plant-secondary">
        <button onClick={whatsappOutreach} className="st-wa" data-testid={`plant-${plant.id}-wa`}>
          <MessageCircle size={16} /> WhatsApp
        </button>
        <button onClick={shareProfile} className="st-share" data-testid={`plant-${plant.id}-share`}>
          <Share2 size={16} /> Share profile
        </button>
      </div>

      <button
        onClick={() => setExpanded((v) => !v)}
        className="st-expand"
        data-testid={`plant-${plant.id}-expand`}
        aria-expanded={expanded}
      >
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        <span>Follow-up tracker</span>
      </button>

      {expanded && (
        <div className="st-followup-grid">
          {["m1", "m2", "m3"].map((m) => (
            <label key={m} className="st-followup-check" data-testid={`plant-${plant.id}-${m}`}>
              <input type="checkbox" checked={!!followups[m]} onChange={() => onFollowupToggle(m)} />
              <span>{m.toUpperCase()} sent</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusButton({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} className={`st-status ${active ? "st-status-active" : ""}`} data-testid={testid}>
      {children}
    </button>
  );
}

function CopyCard({ label, body, testid }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* noop */ }
  }
  return (
    <div className="st-copy" data-testid={testid}>
      <div className="st-copy-label">{label}</div>
      <div className="st-copy-body">{body}</div>
      <button onClick={copy} className="st-copy-btn" data-testid={`${testid}-copy`}>
        {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
      </button>
    </div>
  );
}

function FooterBtn({ icon, label, onClick, testid }) {
  return (
    <button onClick={onClick} className="st-footer-btn" data-testid={testid}>
      {icon}<span>{label}</span>
    </button>
  );
}

function BottomSheet({ children, onClose, testid }) {
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <>
      <div className="st-overlay" onClick={onClose} data-testid={`${testid}-overlay`} />
      <div className="st-sheet" data-testid={testid} role="dialog">
        <button className="st-sheet-close" onClick={onClose} aria-label="Close" data-testid={`${testid}-close`}>
          <X size={18} />
        </button>
        {children}
      </div>
    </>
  );
}

function SignupModal({ plant, onClose, onSuccess }) {
  const [form, setForm] = useState({
    plant_name: plant.name,
    capability: plant.capability,
    contact_name: "",
    contact_email: "",
    whatsapp: "+52",
    industrial_park: plant.park,
    referred_by: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  function update(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    setErr("");
    try {
      await api.post("/sales-toolkit/new-plant-signup", { ...form, captured_by: "field" });
      setDone(true);
      setTimeout(() => onSuccess(), 1400);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Couldn't save. Try once more.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div className="st-overlay" onClick={onClose} data-testid="signup-modal-overlay" />
      <div className="st-modal" role="dialog" data-testid="signup-modal">
        <button className="st-sheet-close" onClick={onClose} aria-label="Close" data-testid="signup-modal-close">
          <X size={18} />
        </button>

        {done ? (
          <div className="st-modal-done" data-testid="signup-modal-success">
            <div className="st-success-check"><Check size={36} /></div>
            <div className="st-success-title">Profile created.</div>
            <div className="st-success-sub">We'll send them a confirmation.</div>
          </div>
        ) : (
          <form onSubmit={submit} className="st-form">
            <div className="st-sheet-title">Create profile · {plant.name}</div>

            <Field label="Plant name" value={form.plant_name} onChange={(v) => update("plant_name", v)} testid="signup-plant-name" required />
            <Field label="Capability / Industry" value={form.capability} onChange={(v) => update("capability", v)} testid="signup-capability" />
            <Field label="Contact name" value={form.contact_name} onChange={(v) => update("contact_name", v)} testid="signup-contact-name" />
            <Field label="Contact email" type="email" value={form.contact_email} onChange={(v) => update("contact_email", v)} testid="signup-contact-email" />
            <Field label="WhatsApp number" value={form.whatsapp} onChange={(v) => update("whatsapp", v)} testid="signup-whatsapp" />

            <label className="st-field">
              <span className="st-field-label">Industrial park</span>
              <select
                value={form.industrial_park}
                onChange={(e) => update("industrial_park", e.target.value)}
                className="st-input"
                data-testid="signup-park"
              >
                <option value="Bermúdez">Bermúdez</option>
                <option value="Salvarcar">Salvarcar</option>
                <option value="Aeropuerto">Aeropuerto</option>
              </select>
            </label>

            <Field
              label="Referred by (optional)"
              value={form.referred_by}
              onChange={(v) => update("referred_by", v)}
              testid="signup-referred-by"
            />

            {err && <div className="st-form-err" data-testid="signup-error">{err}</div>}

            <button type="submit" disabled={submitting} className="st-submit" data-testid="signup-submit">
              {submitting ? "Creating…" : "Create Free Profile →"}
            </button>
          </form>
        )}
      </div>
    </>
  );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
  return (
    <label className="st-field">
      <span className="st-field-label">{label}{required ? " *" : ""}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="st-input"
        data-testid={testid}
        required={required}
      />
    </label>
  );
}

async function copyAndFlash(text, testid) {
  try {
    await navigator.clipboard.writeText(text);
    const el = document.querySelector(`[data-testid="${testid}"]`);
    if (el) { el.classList.add("st-flash"); setTimeout(() => el.classList.remove("st-flash"), 800); }
  } catch { /* noop */ }
}

function Style() {
  return (
    <style>{`
      .st-root {
        background: #0a0f1e; color: #e8edf7;
        font-family: -apple-system, BlinkMacSystemFont, "Inter", sans-serif;
        min-height: 100vh; max-width: 760px; margin: 0 auto;
        padding: 16px 16px 100px; font-size: 15px; line-height: 1.45;
      }
      .st-badge {
        font-family: ui-monospace, SF Mono, monospace;
        font-size: 10px; letter-spacing: 0.14em; color: #3b82f6;
        padding: 6px 10px; border: 1px solid rgba(59,130,246,0.4);
        display: inline-block; margin-bottom: 10px;
      }
      .st-ref-handle {
        display: flex; align-items: center; gap: 6px;
        font-family: ui-monospace, SF Mono, monospace;
        font-size: 10px; letter-spacing: 0.08em; color: #93a3c4;
        margin-bottom: 10px;
      }
      .st-ref-handle label { color: #93a3c4; }
      .st-ref-prefix { color: #3b82f6; }
      .st-ref-input {
        background: rgba(59,130,246,0.08);
        border: 1px solid rgba(59,130,246,0.3);
        color: #22c55e; font-family: inherit; font-size: 11px;
        padding: 4px 8px; border-radius: 4px; outline: none;
        min-width: 80px;
      }
      .st-ref-input:focus { border-color: #3b82f6; }
      .st-alert {
        background: #f59e0b; color: #1f1300;
        padding: 9px 12px; border-radius: 4px;
        font-size: 12px; font-weight: 700; line-height: 1.3;
        margin-bottom: 14px;
      }
      .st-mission {
        background: rgba(59,130,246,0.1);
        border-left: 3px solid #3b82f6;
        padding: 12px 14px; margin-bottom: 22px;
        font-size: 15px; font-weight: 600; line-height: 1.35;
      }
      .st-section { margin-bottom: 28px; }
      .st-h2 {
        font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase;
        color: #93a3c4; font-weight: 600; margin: 0 0 12px;
      }
      .st-stats { display: grid; gap: 8px; grid-template-columns: repeat(3, 1fr); }
      .st-stats-4 { grid-template-columns: repeat(2, 1fr); }
      @media (min-width: 640px) { .st-stats-4 { grid-template-columns: repeat(4, 1fr); } }
      .st-stat {
        background: #111827; border: 1px solid rgba(255,255,255,0.06);
        padding: 12px 10px; border-radius: 6px;
      }
      .st-stat-green { border-color: rgba(34,197,94,0.4); }
      .st-stat-green .st-stat-value { color: #22c55e; }
      .st-stat-value { font-size: 26px; font-weight: 700; line-height: 1; letter-spacing: -0.02em; }
      .st-stat-label { font-size: 10px; color: #93a3c4; margin-top: 6px; text-transform: uppercase; letter-spacing: 0.08em; }
      .st-intel {
        margin-top: 12px; background: rgba(59,130,246,0.08);
        border: 1px solid rgba(59,130,246,0.2); border-radius: 6px;
        padding: 10px 12px; font-size: 13px;
        display: flex; align-items: center; gap: 10px;
      }
      .st-intel-dot {
        width: 6px; height: 6px; border-radius: 50%; background: #3b82f6;
        animation: pulse 1.8s ease-in-out infinite; flex-shrink: 0;
      }
      @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
      .st-progress {
        height: 8px; background: rgba(255,255,255,0.06); border-radius: 100px;
        overflow: hidden; position: relative; margin-bottom: 16px;
      }
      .st-progress-bar { height: 100%; background: #22c55e; transition: width 250ms ease; }
      .st-progress-label {
        position: absolute; inset: 0;
        display: flex; align-items: center; justify-content: center;
        font-size: 10px; letter-spacing: 0.12em; color: #e8edf7;
        text-transform: uppercase; text-shadow: 0 1px 2px rgba(0,0,0,0.5);
      }
      .st-plants { display: flex; flex-direction: column; gap: 10px; }
      .st-plant {
        background: #111827; border: 1px solid rgba(255,255,255,0.06);
        border-radius: 8px; padding: 14px; transition: border-color 200ms;
      }
      .st-plant-visited { border-color: rgba(59,130,246,0.4); }
      .st-plant-signed { border-color: #22c55e; background: rgba(34,197,94,0.06); }
      .st-plant-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
      .st-plant-name { font-size: 17px; font-weight: 700; line-height: 1.2; }
      .st-plant-meta { font-size: 12px; color: #93a3c4; margin-top: 4px; }
      .st-plant-dot { margin: 0 6px; opacity: 0.5; }
      .st-create-profile {
        margin-top: 8px; background: #3b82f6; color: white; border: none;
        padding: 6px 10px; border-radius: 5px; font-size: 11px; font-weight: 700;
        letter-spacing: 0.04em; display: inline-flex; align-items: center; gap: 4px;
        cursor: pointer;
      }
      .st-score {
        min-width: 60px; text-align: center; padding: 6px 8px;
        border-radius: 6px; flex-shrink: 0;
      }
      .st-score-num { font-size: 20px; font-weight: 800; line-height: 1; }
      .st-score-label { font-size: 8px; letter-spacing: 0.14em; margin-top: 3px; }
      .st-score-priority { background: rgba(34,197,94,0.15); color: #22c55e; border: 1px solid rgba(34,197,94,0.3); }
      .st-score-secondary { background: rgba(59,130,246,0.15); color: #3b82f6; border: 1px solid rgba(59,130,246,0.3); }
      .st-score-skip { background: rgba(255,255,255,0.05); color: #6b7280; border: 1px solid rgba(255,255,255,0.08); }
      .st-plant-actions { margin-top: 12px; display: flex; gap: 8px; align-items: center; }
      .st-status-group {
        display: flex; background: rgba(255,255,255,0.04);
        border-radius: 6px; padding: 3px; flex: 1; min-width: 0;
      }
      .st-status {
        flex: 1; background: transparent; border: none; color: #93a3c4;
        font-size: 11px; letter-spacing: 0.04em; padding: 8px 4px;
        border-radius: 4px; cursor: pointer; font-weight: 600;
      }
      .st-status-active { background: #3b82f6; color: white; }
      .st-plant-secondary { margin-top: 8px; display: flex; gap: 8px; }
      .st-wa, .st-share {
        flex: 1; border: none; padding: 10px; border-radius: 6px;
        font-weight: 700; font-size: 12px; display: flex; align-items: center;
        justify-content: center; gap: 6px; cursor: pointer; color: white;
      }
      .st-wa { background: #22c55e; }
      .st-share { background: #3b82f6; }
      .st-expand {
        margin-top: 10px; background: transparent; border: none; color: #93a3c4;
        font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase;
        cursor: pointer; display: flex; align-items: center; gap: 4px; padding: 4px 0;
      }
      .st-followup-grid { margin-top: 8px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; }
      .st-followup-check {
        display: flex; align-items: center; gap: 6px; font-size: 11px;
        padding: 6px 8px; background: rgba(255,255,255,0.04);
        border-radius: 4px; cursor: pointer;
      }
      .st-copy {
        background: #111827; border: 1px solid rgba(255,255,255,0.06);
        border-radius: 8px; padding: 14px; margin-bottom: 10px;
      }
      .st-copy-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.14em; color: #3b82f6; margin-bottom: 8px; }
      .st-copy-body { font-size: 14px; line-height: 1.5; color: #e8edf7; margin-bottom: 10px; white-space: pre-wrap; }
      .st-copy-btn {
        background: #3b82f6; color: white; border: none; padding: 9px 14px;
        border-radius: 6px; font-weight: 600; font-size: 12px;
        display: flex; align-items: center; gap: 6px; cursor: pointer;
      }
      .st-objections {
        background: #111827; border: 1px solid rgba(255,255,255,0.06);
        border-radius: 8px; padding: 12px 14px;
      }
      .st-objections > summary {
        font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase;
        color: #93a3c4; cursor: pointer; list-style: none;
      }
      .st-objection { margin-top: 12px; padding-top: 12px; border-top: 1px solid rgba(255,255,255,0.06); }
      .st-obj-q { font-weight: 600; color: #e8edf7; font-size: 13px; }
      .st-obj-a { color: #93a3c4; margin-top: 4px; font-size: 13px; }
      .st-followup-hint { font-size: 12px; color: #93a3c4; margin-bottom: 12px; }
      .st-roi-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
      .st-roi-card {
        background: #111827; border: 1px solid rgba(255,255,255,0.06);
        border-radius: 8px; padding: 14px;
      }
      .st-roi-bad { border-color: rgba(239,68,68,0.3); }
      .st-roi-good { border-color: rgba(34,197,94,0.3); }
      .st-roi-title { font-size: 13px; font-weight: 700; margin-bottom: 10px; }
      .st-roi-card ul { list-style: none; padding: 0; margin: 0; font-size: 12px; }
      .st-roi-card li { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px dashed rgba(255,255,255,0.06); }
      .st-roi-card li:last-child { border-bottom: none; }
      .st-roi-card li span { color: #93a3c4; }
      .st-roi-card li strong { font-weight: 600; }
      .st-roi-line {
        margin-top: 14px; background: rgba(59,130,246,0.08);
        border: 1px solid rgba(59,130,246,0.3); border-radius: 6px;
        padding: 14px; font-size: 14px; font-weight: 600; text-align: center; line-height: 1.4;
      }
      .st-spacer { height: 80px; }
      .st-footer {
        position: fixed; bottom: 0; left: 0; right: 0;
        background: rgba(10,15,30,0.95); backdrop-filter: blur(12px);
        border-top: 1px solid rgba(255,255,255,0.08);
        padding: 10px 16px; display: flex; justify-content: space-around; z-index: 100;
      }
      .st-footer-btn {
        background: transparent; border: none; color: #e8edf7;
        display: flex; flex-direction: column; align-items: center; gap: 3px;
        padding: 6px 10px; font-size: 10px; letter-spacing: 0.06em;
        text-transform: uppercase; cursor: pointer; transition: color 150ms;
      }
      .st-footer-btn:hover { color: #3b82f6; }
      .st-flash { color: #22c55e !important; }
      /* Overlay + bottom sheets + modal */
      .st-overlay {
        position: fixed; inset: 0; background: rgba(0,0,0,0.55);
        backdrop-filter: blur(2px); z-index: 200;
        animation: fadeIn 180ms ease;
      }
      @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
      .st-sheet, .st-modal {
        position: fixed; left: 0; right: 0; bottom: 0;
        background: #0a0f1e; border-top: 1px solid rgba(255,255,255,0.08);
        border-radius: 16px 16px 0 0; z-index: 210;
        padding: 24px 20px 30px; max-height: 88vh; overflow-y: auto;
        animation: slideUp 240ms cubic-bezier(0.16, 1, 0.3, 1);
      }
      .st-modal { max-width: 540px; margin: 0 auto; }
      .st-sheet-close {
        position: absolute; top: 14px; right: 14px; background: transparent;
        border: none; color: #93a3c4; padding: 6px; cursor: pointer;
      }
      .st-sheet-title { font-size: 16px; font-weight: 700; margin-bottom: 16px; }
      .st-sheet-options { display: flex; flex-direction: column; gap: 8px; }
      .st-sheet-option {
        background: #111827; border: 1px solid rgba(255,255,255,0.06);
        color: #e8edf7; padding: 14px; border-radius: 8px;
        display: flex; align-items: center; gap: 12px; font-size: 14px;
        font-weight: 600; cursor: pointer; text-align: left;
      }
      .st-sheet-option:hover { border-color: #3b82f6; }
      .st-notes-input {
        width: 100%; min-height: 180px; background: #111827;
        border: 1px solid rgba(255,255,255,0.06); border-radius: 8px;
        padding: 12px; color: #e8edf7; font-size: 14px; line-height: 1.45;
        resize: vertical; font-family: inherit;
      }
      .st-notes-actions { display: flex; gap: 8px; margin-top: 12px; }
      .st-notes-copy {
        flex: 1; background: #3b82f6; color: white; border: none;
        padding: 11px; border-radius: 6px; font-weight: 700; font-size: 12px;
        display: flex; align-items: center; justify-content: center; gap: 6px; cursor: pointer;
      }
      .st-notes-clear {
        background: transparent; color: #93a3c4; border: 1px solid rgba(255,255,255,0.1);
        padding: 11px 18px; border-radius: 6px; font-weight: 600; font-size: 12px; cursor: pointer;
      }
      /* Signup form */
      .st-form { display: flex; flex-direction: column; gap: 12px; }
      .st-field { display: flex; flex-direction: column; gap: 4px; }
      .st-field-label { font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: #93a3c4; }
      .st-input {
        background: #111827; border: 1px solid rgba(255,255,255,0.1);
        border-radius: 6px; color: #e8edf7; padding: 12px;
        font-size: 16px; font-family: inherit;
      }
      .st-input:focus { outline: none; border-color: #3b82f6; }
      .st-submit {
        background: #3b82f6; color: white; border: none;
        padding: 14px; border-radius: 8px; font-weight: 800; font-size: 15px;
        margin-top: 8px; cursor: pointer;
      }
      .st-submit:disabled { opacity: 0.6; }
      .st-form-err {
        background: rgba(239,68,68,0.12); border: 1px solid rgba(239,68,68,0.3);
        color: #fca5a5; padding: 10px; border-radius: 6px; font-size: 13px;
      }
      .st-modal-done {
        text-align: center; padding: 24px 16px;
        animation: fadeIn 200ms ease;
      }
      .st-success-check {
        width: 64px; height: 64px; border-radius: 50%; background: #22c55e;
        color: white; display: flex; align-items: center; justify-content: center;
        margin: 0 auto 16px; animation: popIn 320ms cubic-bezier(0.34, 1.56, 0.64, 1);
      }
      @keyframes popIn { from { transform: scale(0); } to { transform: scale(1); } }
      .st-success-title { font-size: 20px; font-weight: 700; }
      .st-success-sub { font-size: 14px; color: #93a3c4; margin-top: 6px; }
      /* Hide site chrome on this page */
      header[data-testid="main-nav"], footer[data-testid="site-footer"], [data-testid="cookie-banner"] {
        display: none !important;
      }
    `}</style>
  );
}
